import { AxiosResponse } from "axios";

import { HALLIDAY_PAYMENTS_API_URL } from "@shared/constants";
import { methodMetadataRegistry } from "../decorators";

/**
 * Configuration options for response validation
 */
export interface ResponseValidationOptions {
    /** Whether to enable response validation */
    enabled: boolean;
    /** Enable debug logging */
    debug?: boolean;
}

/**
 * Default response validation configuration
 */
export const defaultValidationOptions: ResponseValidationOptions = {
    enabled: true,
    debug: false,
};

/**
 * Extracts and normalizes the endpoint from a URL for schema lookup.
 * Handles dynamic path parameters and query strings.
 */
export const extractPathname = (url: string, baseUrl: string = HALLIDAY_PAYMENTS_API_URL): string => {
    if (!url) return "";

    // Remove base URL if present and get just the path
    const urlObj = new URL(url, baseUrl);
    let pathname = urlObj.pathname;

    // Strip dynamic segments by removing common patterns
    pathname = pathname
        // Replace UUIDs (e.g., /quotes/unwant/550e8400-e29b-41d4-a716-446655440000 -> /quotes/unwant)
        .replace(/\/[0-9a-f]{8}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{12}/gi, "");

    return pathname;
};

/**
 * Validates an API response against its expected schema.
 * Logs warnings if validation fails but doesn't throw errors.
 */
export const validateResponse = (
    response: AxiosResponse,
    options: ResponseValidationOptions = defaultValidationOptions
): void => {
    // Skip validation if disabled
    if (!options.enabled) {
        return;
    }

    try {
        const endpoint = extractPathname(response.config.url || "");
        if (!endpoint) {
            if (options.debug) {
                console.debug("[HallidayPaymentsApiClient] No endpoint found in URL:", response.config.url);
            }
            return;
        }

        const httpVerb = response.config.method?.toLowerCase() || "get";
        const key = `${httpVerb} ${endpoint}`;
        const schema = methodMetadataRegistry.get(key)?.outputSchema;
        // Skip validation if no schema is defined for this endpoint
        if (!schema) {
            if (options.debug) {
                console.debug(`[HallidayPaymentsApiClient] No validation schema defined for endpoint: ${endpoint}`);
            }
            return;
        }

        // Perform validation
        const result = schema.safeParse(response.data);
        if (!result.success) {
            console.warn(
                `[HallidayPaymentsApiClient] Response validation failed for ${endpoint}. Your SDK version might be outdated - please check for updates.`
            );
            if (options.debug) {
                console.debug("[HallidayPaymentsApiClient] Response validation error:", result.error);
            }
        }
    } catch (error) {
        // Silently fail validation to not break the main application flow
        if (options.debug) {
            console.debug("[HallidayPaymentsApiClient] Response validation error:", error);
        }
    }
};

/**
 * Creates a response validation interceptor function for axios.
 */
export const createResponseValidationInterceptor = (options: ResponseValidationOptions) => {
    return {
        onFulfilled: <T>(response: AxiosResponse<T>): AxiosResponse<T> => {
            validateResponse(response, options);
            return response;
        },
        onRejected: (error: any) => {
            // pass through the error
            throw error;
        },
    };
};
